import type { ReactNode } from 'react'
import { useParams } from 'react-router'
import { NotFoundState } from '@/features/errors/NotFoundState'
import { t } from '@/lib/i18n'
import { useCommunity } from '@/lib/community'
import { CommunityHeaderCard } from './CommunityHeaderCard'
import { CommunitySidebar } from './CommunitySidebar'
import { Footer } from './Footer'
import { SingleSlot } from './slots'

export interface CommunityColumnsProps {
  /** Classroom, Calendar, Map and Leaderboards use the full 1080 width without a sidebar. */
  fullWidth?: boolean
  /** Hide the compact header card shown below 1024 px (pages with their own big header). */
  headerCard?: boolean
  /** Render the in page "not found" state (unknown community sub path). */
  notFound?: boolean
  children?: ReactNode
}

/** Two columns at >= 1024 px (main max 760, sidebar 280, gap 40), or the full width variant. */
export function CommunityColumns({ fullWidth = false, headerCard = true, notFound = false, children }: CommunityColumnsProps) {
  const { slug = '' } = useParams()
  const { community } = useCommunity(slug)
  const slotProps = { slug: community?.slug ?? slug, community }

  if (notFound) return <NotFoundState />
  if (fullWidth) return <div className="min-w-0">{children}</div>

  return (
    <div className="lg:flex lg:items-start lg:gap-10">
      {headerCard ? (
        <div className="mb-4 lg:hidden">
          <SingleSlot name="CommunityHeaderCard" props={slotProps} fallback={<CommunityHeaderCard {...slotProps} />} />
        </div>
      ) : null}
      <div className="min-w-0 flex-1 lg:max-w-[760px]">
        {children}
        <Footer variant="sidebar" className="mt-10 lg:hidden" />
      </div>
      <aside aria-label={t('About this community')} className="hidden w-[280px] shrink-0 space-y-4 lg:block">
        <SingleSlot name="CommunitySidebar" props={slotProps} fallback={<CommunitySidebar {...slotProps} />} />
        <Footer variant="sidebar" />
      </aside>
    </div>
  )
}
